export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  IDEMPOTENCY_KEY_CONFLICT: 'IDEMPOTENCY_KEY_CONFLICT',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  UNSUPPORTED_SERVER_STATE: 'UNSUPPORTED_SERVER_STATE',
  CONFIGURATION_INVALID: 'CONFIGURATION_INVALID',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  PROJECT_NOT_FOUND: 'PROJECT_NOT_FOUND',
  PROJECT_SLUG_TAKEN: 'PROJECT_SLUG_TAKEN',
  SCRIPT_NOT_FOUND: 'SCRIPT_NOT_FOUND',
  SCRIPT_VERSION_NOT_FOUND: 'SCRIPT_VERSION_NOT_FOUND',
  RELEASE_NOT_FOUND: 'RELEASE_NOT_FOUND',
  RELEASE_NOT_PUBLISHED: 'RELEASE_NOT_PUBLISHED',
  LICENSE_NOT_FOUND: 'LICENSE_NOT_FOUND',
  LICENSE_INVALID_SECRET: 'LICENSE_INVALID_SECRET',
  LICENSE_EXPIRED: 'LICENSE_EXPIRED',
  LICENSE_REVOKED: 'LICENSE_REVOKED',
  LICENSE_SUSPENDED: 'LICENSE_SUSPENDED',
  LICENSE_STATE_INVALID: 'LICENSE_STATE_INVALID',
  LICENSE_SECRET_COLLISION: 'LICENSE_SECRET_COLLISION',
  LICENSE_MACHINE_LIMIT_REACHED: 'LICENSE_MACHINE_LIMIT_REACHED',
  LICENSE_SESSION_NOT_FOUND: 'LICENSE_SESSION_NOT_FOUND',
  LICENSE_SESSION_STALE: 'LICENSE_SESSION_STALE',
  CHECKPOINT_SESSION_NOT_FOUND: 'CHECKPOINT_SESSION_NOT_FOUND',
  CHECKPOINT_NOT_VERIFIED: 'CHECKPOINT_NOT_VERIFIED',
  CHECKPOINT_ALREADY_COMPLETED: 'CHECKPOINT_ALREADY_COMPLETED',
  CHECKPOINT_FLOW_DISABLED: 'CHECKPOINT_FLOW_DISABLED',
  CHECKPOINT_STEP_INVALID: 'CHECKPOINT_STEP_INVALID',
  CHECKPOINT_EXPIRED: 'CHECKPOINT_EXPIRED',
  PROVIDER_UNSUPPORTED: 'PROVIDER_UNSUPPORTED',
  PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
  ENTITLEMENT_LIMIT_REACHED: 'ENTITLEMENT_LIMIT_REACHED',
  SUBSCRIPTION_INACTIVE: 'SUBSCRIPTION_INACTIVE',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  ORDER_NOT_PAYABLE: 'ORDER_NOT_PAYABLE',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  REFUND_NOT_ALLOWED: 'REFUND_NOT_ALLOWED',
  REFUND_ALREADY_SETTLED: 'REFUND_ALREADY_SETTLED',
  BUILD_JOB_NOT_FOUND: 'BUILD_JOB_NOT_FOUND',
  ARTIFACT_NOT_FOUND: 'ARTIFACT_NOT_FOUND',
  ARTIFACT_NOT_READY: 'ARTIFACT_NOT_READY',
  API_KEY_INVALID: 'API_KEY_INVALID',
  API_KEY_EXPIRED: 'API_KEY_EXPIRED',
  SESSION_INVALID: 'SESSION_INVALID',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export type ApiLanguage = 'en' | 'th';

export const DEFAULT_LANGUAGE: ApiLanguage = 'en';

export const SUPPORTED_LANGUAGES: readonly ApiLanguage[] = ['en', 'th'];

export type LocalizeParams = Record<string, string | number | boolean | null | undefined>;

export type ApiErrorDetails = Record<string, unknown>;

export const HTTP_STATUS_BY_CODE: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RESOURCE_CONFLICT: 409,
  IDEMPOTENCY_KEY_CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  REQUEST_TIMEOUT: 408,
  RATE_LIMITED: 429,
  SERVICE_UNAVAILABLE: 503,
  UNSUPPORTED_SERVER_STATE: 502,
  CONFIGURATION_INVALID: 500,
  INTERNAL_ERROR: 500,
  PROJECT_NOT_FOUND: 404,
  PROJECT_SLUG_TAKEN: 409,
  SCRIPT_NOT_FOUND: 404,
  SCRIPT_VERSION_NOT_FOUND: 404,
  RELEASE_NOT_FOUND: 404,
  RELEASE_NOT_PUBLISHED: 409,
  LICENSE_NOT_FOUND: 404,
  LICENSE_INVALID_SECRET: 403,
  LICENSE_EXPIRED: 409,
  LICENSE_REVOKED: 409,
  LICENSE_SUSPENDED: 409,
  LICENSE_STATE_INVALID: 409,
  LICENSE_SECRET_COLLISION: 409,
  LICENSE_MACHINE_LIMIT_REACHED: 409,
  LICENSE_SESSION_NOT_FOUND: 404,
  LICENSE_SESSION_STALE: 409,
  CHECKPOINT_SESSION_NOT_FOUND: 404,
  CHECKPOINT_NOT_VERIFIED: 409,
  CHECKPOINT_ALREADY_COMPLETED: 409,
  CHECKPOINT_FLOW_DISABLED: 409,
  CHECKPOINT_STEP_INVALID: 400,
  CHECKPOINT_EXPIRED: 409,
  PROVIDER_UNSUPPORTED: 400,
  PLAN_NOT_FOUND: 404,
  ENTITLEMENT_LIMIT_REACHED: 402,
  SUBSCRIPTION_INACTIVE: 403,
  ORDER_NOT_FOUND: 404,
  ORDER_NOT_PAYABLE: 409,
  PAYMENT_FAILED: 402,
  REFUND_NOT_ALLOWED: 409,
  REFUND_ALREADY_SETTLED: 409,
  BUILD_JOB_NOT_FOUND: 404,
  ARTIFACT_NOT_FOUND: 404,
  ARTIFACT_NOT_READY: 409,
  API_KEY_INVALID: 401,
  API_KEY_EXPIRED: 401,
  SESSION_INVALID: 401,
  SESSION_EXPIRED: 401,
};

export const ERROR_MESSAGES: Record<ErrorCode, Record<ApiLanguage, string>> = {
  VALIDATION_FAILED: {
    en: 'The request failed validation for one or more fields.',
    th: 'คำขอไม่ผ่านการตรวจสอบข้อมูลในบางฟิลด์',
  },
  UNAUTHORIZED: {
    en: 'Authentication is required to access this resource.',
    th: 'ต้องยืนยันตัวตนก่อนจึงจะเข้าถึงข้อมูลนี้ได้',
  },
  FORBIDDEN: {
    en: 'Your account does not have permission to perform this action.',
    th: 'บัญชีของคุณไม่มีสิทธิ์ในการดำเนินการนี้',
  },
  NOT_FOUND: {
    en: 'The requested resource was not found.',
    th: 'ไม่พบข้อมูลที่คุณขอ',
  },
  METHOD_NOT_ALLOWED: {
    en: 'This HTTP method is not allowed for the requested path.',
    th: 'วิธี HTTP นี้ไม่ได้รับอนุญาตสำหรับเส้นทางที่เรียก',
  },
  UNSUPPORTED_MEDIA_TYPE: {
    en: 'The request content type is not supported for this resource.',
    th: 'ประเภทเนื้อหาของคำขอนี้ไม่ได้รับการรองรับสำหรับทรัพยากรนี้',
  },
  RESOURCE_CONFLICT: {
    en: 'The resource already exists in the required state.',
    th: 'มีข้อมูลอยู่ในสถานะที่ต้องการอยู่แล้ว',
  },
  IDEMPOTENCY_KEY_CONFLICT: {
    en: 'This idempotency key was already used with a different request body.',
    th: 'รหัสมอกันการทำซ้ำนี้ถูกใช้กับคำขอที่มีข้อมูลต่างจากเดิมแล้ว',
  },
  PAYLOAD_TOO_LARGE: {
    en: 'The request body exceeds the allowed size.',
    th: 'ขนาดข้อมูลของคำขอใหญ่เกินกว่าที่กำหนด',
  },
  REQUEST_TIMEOUT: {
    en: 'The request timed out before it could be completed.',
    th: 'คำขอหมดเวลาก่อนที่จะดำเนินการเสร็จ',
  },
  RATE_LIMITED: {
    en: 'Too many requests. Please retry later.',
    th: 'ส่งคำขอมากเกินจำนวนจำกัด กรุณาลองอีกครั้งภายหลัง',
  },
  SERVICE_UNAVAILABLE: {
    en: 'The service is temporarily unavailable.',
    th: 'ระบบไม่พร้อมใช้งานชั่วคราว',
  },
  UNSUPPORTED_SERVER_STATE: {
    en: 'The service returned a state this client does not understand.',
    th: 'ระบบส่งสถานะที่ไคลเอนต์นี้ไม่รองรับกลับมา',
  },
  CONFIGURATION_INVALID: {
    en: 'The server is missing required configuration.',
    th: 'เซิร์ฟเวอร์มีการตั้งค่าไม่ครบถ้วน',
  },
  INTERNAL_ERROR: {
    en: 'An unexpected error occurred.',
    th: 'เกิดข้อผิดพลาดที่ไม่ได้คาดการณ์ไว้',
  },
  PROJECT_NOT_FOUND: {
    en: 'Project {project_id} was not found.',
    th: 'ไม่พบโปรเจกต์ {project_id}',
  },
  PROJECT_SLUG_TAKEN: {
    en: 'The project slug {slug} is already in use.',
    th: 'ชื่อสั้นของโปรเจกต์ {slug} ถูกใช้งานอยู่แล้ว',
  },
  SCRIPT_NOT_FOUND: {
    en: 'Script {script_id} was not found.',
    th: 'ไม่พบสคริปต์ {script_id}',
  },
  SCRIPT_VERSION_NOT_FOUND: {
    en: 'Version {version} of script {script_id} was not found.',
    th: 'ไม่พบเวอร์ชัน {version} ของสคริปต์ {script_id}',
  },
  RELEASE_NOT_FOUND: {
    en: 'Release {release_id} was not found.',
    th: 'ไม่พบเวอร์ชันที่เผยแพร่ {release_id}',
  },
  RELEASE_NOT_PUBLISHED: {
    en: 'Release {release_id} has not been published yet.',
    th: 'เวอร์ชัน {release_id} ยังไม่ได้ถูกเผยแพร่',
  },
  LICENSE_NOT_FOUND: {
    en: 'License {license_id} was not found.',
    th: 'ไม่พบไลเซนส์ {license_id}',
  },
  LICENSE_INVALID_SECRET: {
    en: 'The license secret does not match this project.',
    th: 'คีย์ไลเซนส์ไม่ตรงกับโปรเจกต์นี้',
  },
  LICENSE_EXPIRED: {
    en: 'License {license_id} expired on {expires_at}.',
    th: 'ไลเซนส์ {license_id} หมดอายุเมื่อ {expires_at}',
  },
  LICENSE_REVOKED: {
    en: 'License {license_id} has been revoked.',
    th: 'ไลเซนส์ {license_id} ถูกริบคืนแล้ว',
  },
  LICENSE_SUSPENDED: {
    en: 'License {license_id} is suspended.',
    th: 'ไลเซนส์ {license_id} ถูกพักการใช้งาน',
  },
  LICENSE_STATE_INVALID: {
    en: 'License {license_id} cannot change from {from_status} to {to_status}.',
    th: 'ไลเซนส์ {license_id} ไม่สามารถเปลี่ยนจาก {from_status} เป็น {to_status} ได้',
  },
  LICENSE_SECRET_COLLISION: {
    en: 'The generated license secret collided with an existing license.',
    th: 'คีย์ไลเซนส์ที่สร้างใหม่ชนกับคีย์ที่มีอยู่ในระบบ',
  },
  LICENSE_MACHINE_LIMIT_REACHED: {
    en: 'License {license_id} already reached its limit of {machine_limit} machines.',
    th: 'ไลเซนส์ {license_id} ใช้ครบตามจำนวนเครื่องที่จำกัดไว้ {machine_limit} เครื่องแล้ว',
  },
  LICENSE_SESSION_NOT_FOUND: {
    en: 'License session {session_id} was not found.',
    th: 'ไม่พบเซสชันไลเซนส์ {session_id}',
  },
  LICENSE_SESSION_STALE: {
    en: 'License session {session_id} is stale. Reverify the checkpoint before continuing.',
    th: 'เซสชันไลเซนส์ {session_id} ไม่เป็นปัจจุบัน กรุณายืนยัน checkpoint อีกครั้งก่อนทำต่อ',
  },
  CHECKPOINT_SESSION_NOT_FOUND: {
    en: 'Checkpoint session {session_id} was not found.',
    th: 'ไม่พบเซสชัน checkpoint {session_id}',
  },
  CHECKPOINT_NOT_VERIFIED: {
    en: 'Checkpoint {step_key} has not been verified yet.',
    th: 'checkpoint {step_key} ยังไม่ได้รับการยืนยัน',
  },
  CHECKPOINT_ALREADY_COMPLETED: {
    en: 'Checkpoint session {session_id} is already completed.',
    th: 'เซสชัน checkpoint {session_id} เสร็จสมบูรณ์แล้ว',
  },
  CHECKPOINT_FLOW_DISABLED: {
    en: 'Checkpoint flow {flow_id} is disabled.',
    th: 'ชุด checkpoint {flow_id} ถูกปิดใช้งานไว้',
  },
  CHECKPOINT_STEP_INVALID: {
    en: 'Checkpoint step {step_key} does not belong to flow {flow_id}.',
    th: 'ขั้นตอนนี้ {step_key} ไม่ได้เป็นส่วนหนึ่งของชุด checkpoint {flow_id}',
  },
  CHECKPOINT_EXPIRED: {
    en: 'Checkpoint session {session_id} expired on {expires_at}.',
    th: 'เซสชัน checkpoint {session_id} หมดอายุเมื่อ {expires_at}',
  },
  PROVIDER_UNSUPPORTED: {
    en: 'Provider {provider_id} is not supported.',
    th: 'ไม่รองรับผู้ให้บริการ {provider_id}',
  },
  PLAN_NOT_FOUND: {
    en: 'Plan {plan_id} was not found.',
    th: 'ไม่พบแพ็กเกจ {plan_id}',
  },
  ENTITLEMENT_LIMIT_REACHED: {
    en: 'The {feature_key} limit of {limit} for plan {plan_id} has been reached.',
    th: 'ใช้งาน {feature_key} ครบตามจำนวน {limit} ที่แพ็กเกจ {plan_id} กำหนดแล้ว',
  },
  SUBSCRIPTION_INACTIVE: {
    en: 'Subscription {subscription_id} is not active.',
    th: 'การสมัครสมาชิก {subscription_id} ไม่ได้อยู่ระหว่างใช้งาน',
  },
  ORDER_NOT_FOUND: {
    en: 'Order {order_id} was not found.',
    th: 'ไม่พบใบสั่ง {order_id}',
  },
  ORDER_NOT_PAYABLE: {
    en: 'Order {order_id} cannot be paid in its current state.',
    th: 'ใบสั่ง {order_id} ไม่สามารถชำระเงินได้ในสถานะปัจจุบัน',
  },
  PAYMENT_FAILED: {
    en: 'The payment for order {order_id} failed.',
    th: 'การชำระเงินของใบสั่ง {order_id} ไม่สำเร็จ',
  },
  REFUND_NOT_ALLOWED: {
    en: 'Order {order_id} is not eligible for a refund.',
    th: 'ใบสั่ง {order_id} ไม่มีสิทธิ์ได้รับเงินคืน',
  },
  REFUND_ALREADY_SETTLED: {
    en: 'Refund {refund_id} has already been settled.',
    th: 'การคืนเงิน {refund_id} ดำเนินการเสร็จแล้ว',
  },
  BUILD_JOB_NOT_FOUND: {
    en: 'Build job {build_job_id} was not found.',
    th: 'ไม่พบงานสร้าง {build_job_id}',
  },
  ARTIFACT_NOT_FOUND: {
    en: 'Artifact {artifact_id} was not found.',
    th: 'ไม่พบไฟล์ผลงาน {artifact_id}',
  },
  ARTIFACT_NOT_READY: {
    en: 'Artifact {artifact_id} is not ready for download.',
    th: 'ไฟล์ผลงาน {artifact_id} ยังไม่พร้อมให้ดาวน์โหลด',
  },
  API_KEY_INVALID: {
    en: 'The API key is not recognised.',
    th: 'ไม่รู้จัก API key นี้',
  },
  API_KEY_EXPIRED: {
    en: 'The API key has expired.',
    th: 'API key นี้หมดอายุแล้ว',
  },
  SESSION_INVALID: {
    en: 'The session credential is not recognised.',
    th: 'ไม่รู้จักข้อมูลยืนยันเซสชันนี้',
  },
  SESSION_EXPIRED: {
    en: 'The session has expired. Sign in again.',
    th: 'เซสชันหมดอายุแล้ว กรุณาเข้าสู่ระบบใหม่',
  },
};

const MISSING_INTERPOLATION = '-';

function interpolate(template: string, params: LocalizeParams | undefined): string {
  if (params === undefined) {
    return template;
  }
  const parts = template.split('{');
  if (parts.length === 1) {
    return template;
  }
  let output = parts[0] ?? '';
  for (const part of parts.slice(1)) {
    const close = part.indexOf('}');
    if (close < 0) {
      output += `{${part}`;
      continue;
    }
    const key = part.slice(0, close);
    const value = params[key];
    output += value === null || value === undefined ? MISSING_INTERPOLATION : String(value);
    output += part.slice(close + 1);
  }
  return output;
}

export function isApiLanguage(value: unknown): value is ApiLanguage {
  return value === 'en' || value === 'th';
}

export function normalizeLanguage(value: string | null | undefined): ApiLanguage {
  if (typeof value !== 'string') {
    return DEFAULT_LANGUAGE;
  }
  const primary = value.toLowerCase().split(',')[0]?.trim().split('-')[0];
  if (primary === 'th') {
    return 'th';
  }
  if (primary === 'en') {
    return DEFAULT_LANGUAGE;
  }
  return DEFAULT_LANGUAGE;
}

export function localize(
  code: ErrorCode,
  params: LocalizeParams | undefined,
  lang: ApiLanguage = DEFAULT_LANGUAGE,
): string {
  const entry = ERROR_MESSAGES[code];
  const template = entry[lang] ?? entry[DEFAULT_LANGUAGE] ?? code;
  return interpolate(template, params);
}

export interface ApiErrorInit {
  message?: string | undefined;
  params?: LocalizeParams | undefined;
  details?: ApiErrorDetails | undefined;
  status?: number | undefined;
  language?: ApiLanguage | undefined;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: ApiErrorDetails | undefined;
  readonly params: LocalizeParams | undefined;
  readonly language: ApiLanguage;
  readonly verbatimMessage: boolean;

  constructor(code: ErrorCode, init: ApiErrorInit = {}) {
    const language = init.language ?? DEFAULT_LANGUAGE;
    const message = init.message ?? localize(code, init.params, language);
    super(message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = code;
    this.status = init.status ?? HTTP_STATUS_BY_CODE[code] ?? 500;
    this.details = init.details;
    this.params = init.params;
    this.language = language;
    this.verbatimMessage = init.message !== undefined;
  }

  withLanguage(language: ApiLanguage): ApiError {
    if (this.verbatimMessage || language === this.language) {
      return this;
    }
    return new ApiError(this.code, {
      details: this.details,
      params: this.params,
      status: this.status,
      language,
    });
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export interface ErrorEnvelopeBody {
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetails | undefined;
    request_id?: string | undefined;
  };
}

export interface ErrorResponseOptions {
  language?: ApiLanguage | undefined;
  requestId?: string | undefined;
  exposeInternals?: boolean | undefined;
}

export interface ErrorResponse {
  status: number;
  body: ErrorEnvelopeBody;
  code: ErrorCode;
}

export function toErrorResponse(error: unknown, options: ErrorResponseOptions = {}): ErrorResponse {
  const language = options.language ?? DEFAULT_LANGUAGE;
  const requestId = options.requestId;

  if (isApiError(error)) {
    const localized = error.withLanguage(language);
    const body: ErrorEnvelopeBody = {
      error: {
        code: localized.code,
        message: localized.message,
        ...(localized.details === undefined ? {} : { details: localized.details }),
        ...(requestId === undefined ? {} : { request_id: requestId }),
      },
    };
    return { status: localized.status, body, code: localized.code };
  }

  if (error instanceof Error) {
    const fallback: ErrorEnvelopeBody = {
      error: {
        code: ERROR_CODES.INTERNAL_ERROR,
        message: localize(ERROR_CODES.INTERNAL_ERROR, undefined, language),
        ...(requestId === undefined ? {} : { request_id: requestId }),
        ...(options.exposeInternals === true
          ? { details: { reason: error.name, message: error.message } }
          : {}),
      },
    };
    return {
      status: HTTP_STATUS_BY_CODE.INTERNAL_ERROR,
      body: fallback,
      code: ERROR_CODES.INTERNAL_ERROR,
    };
  }

  const unknownBody: ErrorEnvelopeBody = {
    error: {
      code: ERROR_CODES.INTERNAL_ERROR,
      message: localize(ERROR_CODES.INTERNAL_ERROR, undefined, language),
      ...(requestId === undefined ? {} : { request_id: requestId }),
    },
  };
  return {
    status: HTTP_STATUS_BY_CODE.INTERNAL_ERROR,
    body: unknownBody,
    code: ERROR_CODES.INTERNAL_ERROR,
  };
}

export function apiErrorFromEnvelope(
  code: string,
  message: string,
  details?: ApiErrorDetails | undefined,
): ApiError {
  const known = Object.values(ERROR_CODES).includes(code as ErrorCode);
  return new ApiError((known ? code : ERROR_CODES.INTERNAL_ERROR) as ErrorCode, {
    message,
    details,
  });
}
